import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useLocation } from "react-router-dom";
import { queryParam } from "../components/links";
import { Card, DataTable, PageHeader, Button, Badge, Money } from "../components/ui";
import Modal, { FormField, FormRow, Input, Select, TextArea, SubmitButton, ErrorBanner } from "../components/Modal";
import FinTable from "../components/FinTable";
import t from "../components/theme";

const INSTRUMENT_TYPES = [
  { value: "Stock", label: "Stock" },
  { value: "Note", label: "Convertible Note" },
  { value: "Warrant", label: "Warrant" },
  { value: "ELOC", label: "ELOC" },
  { value: "Option", label: "Option" },
  { value: "Preferred", label: "Preferred Stock" },
];


// ── Price freshness ───────────────────────────────────────────────────────
//
// The page used to print `last_price` as a bare number, and `last_price_date`
// was not in the list payload at all — so the field that decides whether the
// price means anything could not be shown, on the page whose "Set" button
// writes it (D-185).
//
// Two rules the column obeys rather than hides:
//
// And the price it shows is the one the **valuation** uses — a hand-set mark
// or Clear Street's close, then PriceHistory, then `SecurityDefinition.
// last_price` last — resolved server-side by the one function that knows the
// order. The first version of this column read that last field alone and
// printed a dash for every security on the book.
//
//   * an undated price is **unusable**, not current. §0.30:
//     `NAVEngine._get_last_price` falls back to `last_price` with no date
//     test, so a report run as at 31 July marks July's position on today's
//     close. A price with no date cannot answer an as-of question, and this
//     says so instead of showing a tick beside it;
//   * "no price" is two different facts. A listed equity with none is a gap
//     with a remedy; a private note, preferred, PPLN or LP interest has none
//     because none exists. §0.17 drew exactly this line for the price
//     scheduler — it reads FAILED for a security with a symbol and no price
//     "but not for the standing backlog of securities with no yahoo_symbol,
//     because a report that fails every morning stops being read."
//
// The server decides which of the five a row is; this only draws it, so the
// page and the scheduler cannot come to different conclusions about the same
// security.
const PRICE_STATUS = {
  current: { label: "Current", variant: "green" },
  stale: { label: "Stale", variant: "yellow" },
  undated: { label: "No date", variant: "red" },
  expected_missing: { label: "Missing", variant: "red" },
  none_expected: { label: "None expected", variant: "gray" },
};

//: The statuses that are somebody's work. `none_expected` is deliberately not
//: among them — it is a ruling about the instrument, not a gap, and a filter
//: that swept it in would return most of the book every day (§0.30).
const NEEDS_A_PRICE = ["expected_missing", "undated", "stale"];

function PriceStatus({ row }) {
  const meta = PRICE_STATUS[row.price_status];
  if (!meta) return <span style={{ color: t.textSecondary }}>—</span>;
  const age =
    row.price_age_days === null || row.price_age_days === undefined
      ? null
      : row.price_age_days === 0
      ? "today"
      : `${row.price_age_days}d old`;
  return (
    <span
      title={row.price_status_reads_as}
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <Badge text={meta.label} variant={meta.variant} />
      {age && (
        <span style={{ fontSize: 11, color: t.textSecondary, fontFamily: t.fontMono }}>
          {age}
        </span>
      )}
    </span>
  );
}

export default function Securities() {
  const [securities, setSecurities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  // Seeded from `?q=`, so a link from the global search lands filtered.
  const location = useLocation();
  const [query, setQuery] = useState(() => queryParam(location.search));
  useEffect(() => { setQuery(queryParam(location.search)); }, [location.search]);
  // Off by default: the page is a register first and a work list second.
  // `?needs=price` opens the list on what needs a price — where the
  // Dashboard's stale-price alert sends the reader.
  const [needsPrice, setNeedsPrice] = useState(() => queryParam(window.location.search, "needs") === "price");
  // Setting a price by hand.
  //
  // Brian, 17 September 2026, on Valuno Group AB (publ): the feed has the wrong
  // listing and the price he can read is on his broker's screen. Editing the
  // number on this page used to change the screen and not the book — the
  // valuation reads `PriceHistory` first, so a stale fetched row went on
  // winning. This writes the record the valuation actually reads.
  const [pricing, setPricing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/securities/securities/").then((r) => {
      setSecurities(r.data.results || r.data);
      setLoading(false);
    });
  };

  useEffect(load, []);

  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const byText = words.length
    ? securities.filter((s) => {
        const text = [s.ticker, s.name, s.issuer, s.instrument_type].filter(Boolean).join(" ").toLowerCase();
        return words.every((w) => text.includes(w));
      })
    : securities;
  const byType = filter === "all" ? byText : byText.filter((s) => s.instrument_type === filter);
  const filtered = needsPrice
    ? byType.filter((s) => NEEDS_A_PRICE.includes(s.price_status))
    : byType;
  const needsPriceCount = securities.filter((s) => NEEDS_A_PRICE.includes(s.price_status)).length;

  const handleRowClick = (row) => {
    // Fetch full detail (list serializer is slim)
    api.get(`/securities/securities/${row.id}/`).then((r) => setEditing(r.data));
  };

  return (
    <div>
      <PageHeader
        title="Securities"
        subtitle="Security master — stocks, notes, warrants, ELOCs"
        action={<Button onClick={() => setShowAdd(true)}>+ Add Security</Button>}
      />

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {["all", "Stock", "Note", "Warrant", "ELOC"].map((t) => (
          <button key={t} onClick={() => setFilter(t)} style={{
            padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db",
            background: filter === t ? "#2563eb" : "#fff",
            color: filter === t ? "#fff" : "#334155",
            fontSize: 12, fontWeight: 500, cursor: "pointer", textTransform: "capitalize",
          }}>{t === "all" ? "All" : t}</button>
        ))}
        <input
          type="search"
          aria-label="Search securities"
          placeholder="Search ticker, name or issuer…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginLeft: 8, width: 240, maxWidth: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, font: "inherit", fontSize: 12 }}
        />
        <button
          onClick={() => setNeedsPrice((v) => !v)}
          title="A price is expected and missing, is undated, or is more than ten days old. Instruments that never have a price are not in this count."
          style={{
            marginLeft: "auto", padding: "6px 14px", borderRadius: 6,
            border: "1px solid #d1d5db",
            background: needsPrice ? "#b45309" : "#fff",
            color: needsPrice ? "#fff" : "#334155",
            fontSize: 12, fontWeight: 500, cursor: "pointer",
          }}
        >Needs a price{needsPriceCount ? ` (${needsPriceCount})` : ""}</button>
      </div>

      <Card>
        <DataTable
          columns={[
            { key: "ticker", label: "Ticker" },
            { key: "name", label: "Name" },
            { key: "instrument_type", label: "Type", render: (v) => <Badge text={v} variant={
              v === "Stock" ? "blue" : v === "Note" ? "purple" : v === "Warrant" ? "yellow" : v === "ELOC" ? "green" : "gray"
            } /> },
            { key: "issuer", label: "Issuer" },
            // `price`, not `last_price`. The column read the model field, which
            // is the *fourth and last* source the valuation consults and is
            // null across this whole book — so the page printed a dash for
            // every security while NAV had a price for most of them (D-185 §7).
            { key: "price", label: "Price", align: "right", render: (v, row) => (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                {v ? <Money value={v} /> : "—"}
                <button
                  onClick={(e) => { e.stopPropagation(); setPricing({ security: row, price: "" }); }}
                  title="Set this price by hand — writes the dated mark the valuation reads"
                  style={{
                    border: `1px solid ${t.border}`, background: t.bgCard, cursor: "pointer",
                    borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600,
                    color: t.textSecondary, fontFamily: t.fontBody,
                  }}
                >Set</button>
              </span>
            ) },
            { key: "price_date", label: "As of", align: "right", render: (v, row) => (
              <span
                title={row.price_source ? `source: ${row.price_source}` : undefined}
                style={{ fontFamily: t.fontMono, fontSize: 12, color: v ? t.textPrimary : t.textSecondary }}
              >
                {v || "—"}
              </span>
            ) },
            { key: "price_status", label: "Price status", render: (_v, row) => <PriceStatus row={row} /> },
            { key: "sector", label: "Sector", render: (v) => v || "—" },
          ]}
          rows={filtered}
          onRowClick={handleRowClick}
          emptyMessage={
            needsPrice
              ? "Every security here has a usable price, or is one no price is expected for."
              : "No securities yet — click + Add Security to create one"
          }
          loading={loading}
        />
      </Card>

      {pricing && <SetPriceModal state={pricing} setState={setPricing}
        onClose={() => setPricing(null)} onSaved={() => { setPricing(null); load(); }} />}
      {showAdd && <SecurityModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <SecurityModal security={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function SecurityModal({ security, onClose, onSaved }) {
  const isEdit = !!security;
  const [form, setForm] = useState({
    ticker: security?.ticker || "",
    name: security?.name || "",
    instrument_type: security?.instrument_type || "Stock",
    issuer: security?.issuer || "",
    cusip: security?.cusip || "",
    sector: security?.sector || "",
    strategy_tag: security?.strategy_tag || "",
    last_price: security?.last_price || "",
    // Note
    coupon_rate: security?.coupon_rate || "",
    day_count: security?.day_count || "30/360",
    maturity_date: security?.maturity_date || "",
    issue_date: security?.issue_date || "",
    principal_amount: security?.principal_amount || "",
    conversion_price: security?.conversion_price || "",
    interest_mode: security?.interest_mode || "Cash",
    // Warrant
    strike_price: security?.strike_price || "",
    warrant_shares: security?.warrant_shares || "",
    warrant_expiry: security?.warrant_expiry || "",
    // ELOC
    eloc_commitment_amount: security?.eloc_commitment_amount || "",
    eloc_pricing_days: security?.eloc_pricing_days || "3",
    eloc_discount_pct: security?.eloc_discount_pct || "",
    eloc_settlement_days: security?.eloc_settlement_days || "2",
    // General
    notes: security?.notes || "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [showAddTier, setShowAddTier] = useState(false);
  const [newTier, setNewTier] = useState({ conversion_price: "", conversion_ratio: "", condition_type: "", condition_description: "", effective_date: "", expiry_date: "", floor_price: "", ceiling_price: "", discount_to_market_pct: "", vwap_lookback_days: "", notes: "" });

  const handleDelete = async () => {
    try {
      // Preview what will be deleted
      const preview = await api.get(`/securities/securities/${security.id}/cascade/`);
      const s = preview.data.will_delete;
      const unlinkedDeals = (s.deals_to_unlink || []).map(d => d.name).join(", ");
      const parts = [];
      if (unlinkedDeals) parts.push(`\u2022 Deals to unlink (NOT deleted): ${unlinkedDeals}`);
      if (s.deal_securities) parts.push(`\u2022 ${s.deal_securities} deal-security link(s)`);
      if (s.tax_lots) parts.push(`\u2022 ${s.tax_lots} tax lot(s)`);
      if (s.price_history) parts.push(`\u2022 ${s.price_history} price record(s)`);
      if (s.compliance_checks) parts.push(`\u2022 ${s.compliance_checks} compliance check(s)`);
      const detail = parts.length > 0 ? `\n\nThis will also delete:\n${parts.join("\n")}` : "";
      if (!window.confirm(`Delete security "${s.ticker} \u2014 ${s.name}"?${detail}\n\nThis cannot be undone.`)) return;
      await api.delete(`/securities/securities/${security.id}/cascade/`);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      alert("Delete failed: " + msg);
    }
  };

  // Load conversion price tiers when editing
  React.useEffect(() => {
    if (isEdit && security?.id) {
      api.get(`/securities/conversion-tiers/?security=${security.id}`)
        .then(r => setTiers(r.data.results || r.data || []))
        .catch(() => {});
    }
  }, [isEdit, security?.id]);

  const set = (field) => (val) => setForm({ ...form, [field]: val });

  const save = () => {
    if (!form.ticker || !form.name || !form.issuer) {
      return setError("Ticker, name, and issuer are required");
    }
    setSaving(true);
    setError("");

    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) payload[k] = v;
    });

    const req = isEdit
      ? api.patch(`/securities/securities/${security.id}/`, payload)
      : api.post("/securities/securities/", payload);

    req.then(() => onSaved())
      .catch((err) => {
        setSaving(false);
        const data = err.response?.data;
        setError(data ? JSON.stringify(data) : "Failed to save");
      });
  };

  const type = form.instrument_type;

  return (
    <Modal title={isEdit ? `Edit — ${security.ticker}` : "Add Security"} onClose={onClose} width={560}>
      <ErrorBanner message={error} />

      <FormRow>
        <FormField label="Ticker" required>
          <Input value={form.ticker} onChange={set("ticker")} placeholder="e.g. NVX" />
        </FormField>
        <FormField label="Instrument Type" required>
          <Select value={form.instrument_type} onChange={set("instrument_type")} options={INSTRUMENT_TYPES} />
        </FormField>
      </FormRow>
      <FormField label="Security Name" required>
        <Input value={form.name} onChange={set("name")} placeholder="e.g. NovaTech Inc Common Stock" />
      </FormField>
      <FormRow>
        <FormField label="Issuer" required>
          <Input value={form.issuer} onChange={set("issuer")} placeholder="e.g. NovaTech Inc" />
        </FormField>
        <FormField label="CUSIP">
          <Input value={form.cusip} onChange={set("cusip")} placeholder="Optional" />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Sector">
          <Input value={form.sector} onChange={set("sector")} placeholder="e.g. Technology" />
        </FormField>
        {/* This writes `SecurityDefinition.last_price`, which the valuation
            consults **last** — behind a hand-set mark, Clear Street's close
            and PriceHistory. It is a fallback, not the way to price
            something, and the "Set" button on the table is. Saying so on the
            field, because a form that looks like the price and is the last
            resort is the trap this page already had once (D-185). */}
        <FormField label="Fallback price">
          <Input type="number" value={form.last_price} onChange={set("last_price")} step="0.01" placeholder="Used only if nothing else has a price" />
          <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 4 }}>
            The valuation reads a hand-set mark, then Clear Street, then the
            price feed, then this. To price a security, use <strong>Set</strong>
            on the table — it writes a dated mark.
          </div>
        </FormField>
      </FormRow>

      {type === "Note" && (
        <>
          <SectionHeader label="Note Details" />
          <FormRow>
            <FormField label="Principal Amount">
              <Input type="number" value={form.principal_amount} onChange={set("principal_amount")} step="0.01" />
            </FormField>
            <FormField label="Coupon Rate">
              <Input type="pct" value={form.coupon_rate} onChange={set("coupon_rate")} placeholder="8" />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Day Count">
              <Select value={form.day_count} onChange={set("day_count")} options={[
                { value: "30/360", label: "30/360" },
                { value: "Actual/360", label: "Actual/360" },
                { value: "Actual/365", label: "Actual/365" },
              ]} />
            </FormField>
            <FormField label="Interest Mode">
              <Select value={form.interest_mode} onChange={set("interest_mode")} options={[
                { value: "Cash", label: "Cash" },
                { value: "PIK", label: "PIK" },
                { value: "Cash/PIK", label: "Cash/PIK" },
              ]} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Issue Date">
              <Input type="date" value={form.issue_date} onChange={set("issue_date")} />
            </FormField>
            <FormField label="Maturity Date">
              <Input type="date" value={form.maturity_date} onChange={set("maturity_date")} />
            </FormField>
          </FormRow>
          <FormField label="Conversion Price (Primary)">
            <Input type="number" value={form.conversion_price} onChange={set("conversion_price")} step="0.01" placeholder="$ per share" />
          </FormField>

          {isEdit && (
            <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 16, paddingTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>Conversion Price Schedule ({tiers.length} tier{tiers.length !== 1 ? "s" : ""})</div>
                <button type="button" onClick={() => setShowAddTier(!showAddTier)} style={{ padding: "4px 12px", borderRadius: 5, border: "1px solid #7c3aed", background: "transparent", color: "#7c3aed", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{showAddTier ? "Cancel" : "+ Add Tier"}</button>
              </div>
              {tiers.length === 0 && !showAddTier && <div style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic", marginBottom: 8 }}>No additional tiers. Primary conversion price above is used by default.</div>}
              {tiers.map((t, i) => (
                <div key={t.id} style={{ padding: 10, marginBottom: 8, background: "#faf5ff", borderRadius: 6, border: "1px solid #e9d5ff", fontSize: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, color: "#7c3aed" }}>Tier {i + 1}{t.condition_type ? " \u2014 " + t.condition_type : ""}</span>
                    <button type="button" onClick={async () => { if (window.confirm("Remove this tier?")) { await api.delete("/securities/conversion-tiers/" + t.id + "/"); setTiers(tiers.filter(x => x.id !== t.id)); }}} style={{ background: "none", border: "none", color: "#dc2626", fontSize: 14, cursor: "pointer", fontWeight: 700 }}>&times;</button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", lineHeight: 1.8 }}>
                    {t.conversion_price && <div><span style={{ color: "#64748b" }}>Price:</span> <strong>${Number(t.conversion_price).toFixed(4)}</strong></div>}
                    {t.conversion_ratio && <div><span style={{ color: "#64748b" }}>Ratio:</span> <strong>{Number(t.conversion_ratio).toLocaleString()}</strong></div>}
                    {t.floor_price && <div><span style={{ color: "#64748b" }}>Floor:</span> ${Number(t.floor_price).toFixed(4)}</div>}
                    {t.ceiling_price && <div><span style={{ color: "#64748b" }}>Ceiling:</span> ${Number(t.ceiling_price).toFixed(4)}</div>}
                    {t.discount_to_market_pct && <div><span style={{ color: "#64748b" }}>Discount:</span> {(Number(t.discount_to_market_pct) * 100).toFixed(1)}%</div>}
                    {t.vwap_lookback_days && <div><span style={{ color: "#64748b" }}>VWAP Lookback:</span> {t.vwap_lookback_days} days</div>}
                    {t.effective_date && <div><span style={{ color: "#64748b" }}>Effective:</span> {t.effective_date}</div>}
                    {t.expiry_date && <div><span style={{ color: "#64748b" }}>Expires:</span> {t.expiry_date}</div>}
                  </div>
                  {t.condition_description && <div style={{ marginTop: 4, color: "#475569" }}><span style={{ color: "#64748b" }}>Condition:</span> {t.condition_description}</div>}
                  {t.notes && <div style={{ marginTop: 2, color: "#475569", fontStyle: "italic" }}>{t.notes}</div>}
                </div>
              ))}
              {showAddTier && (
                <div style={{ padding: 12, background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd", marginTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#0369a1", marginBottom: 8 }}>New Conversion Tier</div>
                  <FormRow>
                    <FormField label="Conversion Price"><Input type="number" value={newTier.conversion_price} onChange={(v) => setNewTier({...newTier, conversion_price: v})} step="0.0001" placeholder="$0.00" /></FormField>
                    <FormField label="Conversion Ratio"><Input type="number" value={newTier.conversion_ratio} onChange={(v) => setNewTier({...newTier, conversion_ratio: v})} placeholder="shares per unit" /></FormField>
                  </FormRow>
                  <FormRow>
                    <FormField label="Condition Type">
                      <Select value={newTier.condition_type} onChange={(v) => setNewTier({...newTier, condition_type: v})} options={[{value:"",label:"\u2014 Select \u2014"},{value:"Fixed",label:"Fixed Price"},{value:"Variable",label:"Variable / Formula"},{value:"Time-Based",label:"Time-Based"},{value:"Milestone",label:"Milestone-Triggered"},{value:"Price-Based",label:"Price-Based Reset"},{value:"Stockholder Approval",label:"Stockholder Approval"}]} />
                    </FormField>
                    <FormField label="Discount to Market %"><Input type="pct" value={newTier.discount_to_market_pct} onChange={(v) => setNewTier({...newTier, discount_to_market_pct: v})} placeholder="10" /></FormField>
                  </FormRow>
                  <FormField label="Condition Description"><TextArea value={newTier.condition_description} onChange={(v) => setNewTier({...newTier, condition_description: v})} placeholder="e.g. Applies if converted before maturity; resets to lower of fixed or 90% of 5-day VWAP" rows={2} /></FormField>
                  <FormRow>
                    <FormField label="Floor Price"><Input type="number" value={newTier.floor_price} onChange={(v) => setNewTier({...newTier, floor_price: v})} step="0.0001" /></FormField>
                    <FormField label="Ceiling Price"><Input type="number" value={newTier.ceiling_price} onChange={(v) => setNewTier({...newTier, ceiling_price: v})} step="0.0001" /></FormField>
                  </FormRow>
                  <FormRow>
                    <FormField label="VWAP Lookback (days)"><Input type="number" value={newTier.vwap_lookback_days} onChange={(v) => setNewTier({...newTier, vwap_lookback_days: v})} placeholder="5" /></FormField>
                    <FormField label="Effective Date"><Input type="date" value={newTier.effective_date} onChange={(v) => setNewTier({...newTier, effective_date: v})} /></FormField>
                  </FormRow>
                  <FormField label="Notes"><TextArea value={newTier.notes} onChange={(v) => setNewTier({...newTier, notes: v})} rows={2} /></FormField>
                  <button type="button" onClick={async () => { try { const payload={security:security.id,tier_order:tiers.length+1}; Object.entries(newTier).forEach(([k,v])=>{if(v!==""&&v!==null&&v!==undefined)payload[k]=v;}); const res=await api.post("/securities/conversion-tiers/",payload); setTiers([...tiers,res.data]); setShowAddTier(false); setNewTier({conversion_price:"",conversion_ratio:"",condition_type:"",condition_description:"",effective_date:"",expiry_date:"",floor_price:"",ceiling_price:"",discount_to_market_pct:"",vwap_lookback_days:"",notes:""}); } catch(err){alert("Failed: "+(err.response?.data?JSON.stringify(err.response.data):err.message));}}} style={{ marginTop: 8, padding: "8px 20px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Save Tier</button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {type === "Warrant" && (
        <>
          <SectionHeader label="Warrant Details" />
          <FormRow>
            <FormField label="Strike Price">
              <Input type="number" value={form.strike_price} onChange={set("strike_price")} step="0.01" />
            </FormField>
            <FormField label="Warrant Shares">
              <Input type="number" value={form.warrant_shares} onChange={set("warrant_shares")} />
            </FormField>
          </FormRow>
          <FormField label="Expiry Date">
            <Input type="date" value={form.warrant_expiry} onChange={set("warrant_expiry")} />
          </FormField>
        </>
      )}

      {type === "ELOC" && (
        <>
          <SectionHeader label="ELOC Details" />
          <FormRow>
            <FormField label="Commitment Amount">
              <Input type="number" value={form.eloc_commitment_amount} onChange={set("eloc_commitment_amount")} step="0.01" />
            </FormField>
            <FormField label="Discount %">
              <Input type="pct" value={form.eloc_discount_pct} onChange={set("eloc_discount_pct")} placeholder="3" />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Pricing Days">
              <Input type="number" value={form.eloc_pricing_days} onChange={set("eloc_pricing_days")} />
            </FormField>
            <FormField label="Settlement Days">
              <Input type="number" value={form.eloc_settlement_days} onChange={set("eloc_settlement_days")} />
            </FormField>
          </FormRow>
        </>
      )}

      <FormField label="Notes">
        <TextArea value={form.notes} onChange={set("notes")} placeholder="Optional notes" />
      </FormField>

      <SubmitButton onClick={save} loading={saving} label={isEdit ? "Save Changes" : "Create Security"} />
      {isEdit && (
        <button onClick={handleDelete} style={{
          width: "100%", padding: "10px 0", marginTop: 8, borderRadius: 6,
          border: "1px solid #dc2626", background: "transparent",
          color: "#dc2626", fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}>
          Delete Security
        </button>
      )}
    </Modal>
  );
}

function SectionHeader({ label }) {
  return (
    <div style={{ borderTop: "1px solid #e5e7eb", margin: "16px 0", paddingTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>{label}</div>
    </div>
  );
}


/* ── Set a price by hand ──────────────────────────────────────────────────
   The valuation reads `PriceHistory`, so this writes a dated row there and
   labels it `Manual`. That row beats a fetch of the same day, because a price
   somebody set is a decision and a fetch is a job. The first click asks the
   server what it would replace and writes nothing. */
function SetPriceModal({ state, setState, onClose, onSaved }) {
  const { security } = state;
  const [price, setPrice] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const call = async (confirm) => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/securities/securities/${security.id}/set-price/`,
        { price, confirm });
      if (confirm) { onSaved(); } else { setPreview(r.data); }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal title={`Set price — ${security.ticker || security.name}`} onClose={onClose} width={480}>
      <ErrorBanner message={error} />
      <FormField label={`Price${security.currency ? ` (${security.currency})` : ""}`}>
        <Input type="number" step="any" value={price} autoFocus
          onChange={(e) => { setPrice(e.target.value); setPreview(null); }}
          placeholder="e.g. 0.09" />
      </FormField>
      {preview && (
        <div style={{ fontSize: 12, color: t.textSecondary, margin: "10px 0" }}>
          {preview.replaces?.price
            ? <>Replaces <strong>{preview.replaces.price}</strong> from {preview.replaces.as_at || "an undated source"} ({preview.replaces.source}).</>
            : <>Nothing is priced here yet.</>}
          <div style={{ marginTop: 6 }}>
            Recorded as at <strong>{preview.as_at}</strong>, source Manual.
          </div>
          {preview.still_fetched && (
            <div style={{ marginTop: 6, color: "#b45309" }}>
              This security still has a price feed symbol, so a later fetch will write
              over this on a future date. Fix the symbol to stop that.
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        {!preview
          ? <SubmitButton onClick={() => call(false)} disabled={busy || !price}>Check</SubmitButton>
          : <SubmitButton onClick={() => call(true)} disabled={busy}>Set price</SubmitButton>}
        <button onClick={onClose} style={{
          padding: "8px 16px", borderRadius: 6, border: `1px solid ${t.border}`,
          background: "transparent", color: t.textSecondary, fontSize: 13,
          fontWeight: 600, cursor: "pointer",
        }}>Cancel</button>
      </div>
    </Modal>
  );
}
